import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Assets (swap paths if your structure differs)
import footer_vid from "./animations/Footer_Vid.mp4";
import efficiencyIcon from "../images/efficiency.png";
import aiAutomationIcon from "../images/AI_Automation.png";
import hipaaIcon from "../images/HIPAA_Compliant.png";
import growth from "../images/growth.jpg";
import board from "../images/board.jpg";
import cms from "../images/CMS.png";
import meeting from "../images/OfficeMeeting.jpg";

import { pageVariants } from "./animations/pageTransitions";
import ScrollReveal from "./animations/ScrollReveal";
import AnimatedCard from "./AnimatedCard";
import HorizontalDragger from "./HorizontalDragger";
import HorizontalSnap from "./HorizontalSnap";
import ParallaxBand from "./ParallaxBand";
import AutoScrollSection from './animations/AutoScrollSection';

// Styles
import "./HomePage.css";

/**
 * Full HomePage.js — BCG-style refresh
 * - Full-width video hero with centered copy
 * - Floating interactive tiles along the hero bottom
 * - Feature cards grid
 * - Spotlight grid (clickable cards)
 * - CTA band
 */
export default function HomePage() {
  const navigate = useNavigate();
  const [capability, setCapability] = useState("");
  const [industry, setIndustry] = useState("");


  return (
    <div className="homepage-container">
      {/* ===== HERO ===== */}
      <section className="hero-section">
        {/* <video autoPlay loop muted playsInline className="hero-video">
          <source src={footer_vid} type="video/mp4" />
        </video> */}

        <h3 className="firstline">Welcome to S&S Healthcare Consulting</h3>

        <div className="hero-text">
          <h3 className="line1">Innovating Automation, Empowering Growth,</h3>
          <h3 className="line2">Transforming Tomorrow</h3>
        </div>


        {/* ===== TOPICS (placeholder for your AutoScrollSection) ===== */}
        <div className="hero-sub">
          {/* <h2>Editorials To Read</h2> */}
          <AutoScrollSection></AutoScrollSection>
        </div>


        {/* Floating interactive tiles */}
        {/* <div className="interactive-tiles">
          {tiles.map((t) => (
            <button key={t.title} className="floaty-tile" onClick={() => navigate("/form-page")}>
              <img src={t.img} alt="" />
              <div>
                <span>{t.title}</span>
                <small>{t.caption}</small>
              </div>
            </button>
          ))}
        </div> */}
      </section>

      {/* ===== ASSIST (BCG-style split section) ===== */}
      <section className="assist-section">
        <div className="assist-inner">
          <div className="assist-left">
            <h2 className="assist-title">How can we assist you today?</h2>
            <p className="assist-subtitle">
              Explore our core capabilities and healthcare focus areas. Choose a topic to get a tailored plan.
            </p>

            <div className="assist-controls">
              <label className="assist-select">
                <span>Services & Software</span>
                <select
                  value={capability}
                  onChange={(e) => setCapability(e.target.value)}
                >
                  <option value="">Services & Software</option>
                  {capabilities.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="assist-select">
                <span>Provider Type</span>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <option value="">Provider Type</option>
                  {industries.map((i) => (
                    <option key={i.value} value={i.value}>{i.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="assist-actions">
              <button
                className="cta-button"
                disabled={!capability && !industry}
                onClick={() => navigate("/form-page", { state: { capability, industry } })}
              >
                Continue
              </button>
              <button
                className="hero-button alt"
                onClick={() => navigate("/form-page")}
              >
                Talk to Us
              </button>
            </div>
          </div>

          <div className="assist-right" aria-hidden="true">
            <img className="assist-image" src={meeting} alt="" />
          </div>
        </div>
      </section>


      {/* ===== FEATURES ===== */}
      {/* ===== WHY CHOOSE US (clean rail layout) ===== */}
      <section className="why2-section">
        <div className="why2-inner">
          <header className="why2-header">
            <h2>WHY ORGANIZATIONS CHOOSE S&S HEALTHCARE CONSULTING</h2>
            <p>
              Purpose-built AI, automation, and compliance expertise—designed for real-world healthcare delivery.
            </p>
          </header>

          <div className="why2-rail">
            {iconFeatures.map((f) => (
              <motion.div
                key={f.title}
                className="why2-item"
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 320, damping: 24 }}
              >
                <div className="why2-icon">
                  <img src={f.icon} alt={f.title} />
                </div>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </motion.div>
            ))}
          </div>

          {/* <div className="why2-cta">
            <button className="cta-button" onClick={() => navigate("/form-page")}>
              Get a tailored plan
            </button>
          </div> */}
        </div>
      </section>

      {/* ===== SPOTLIGHTS ===== */}
{/*       <section className="spotlight">
        <h2>Where We Help Most</h2>
        <p>Hover to explore, click to dive deeper.</p>
        <div className="spotlight-grid">
          {spotlights.map((s) => (
            <motion.article
              key={s.title}
              className="spotlight-card"
              whileHover={{ y: -6 }}
              onClick={() => navigate("/form-page")}
            >
              <div className="spotlight-media">
                <img src={s.img} alt="" />
              </div>
              <div className="spotlight-copy">
                <h3>{s.title}</h3>
                <p>{s.blurb}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section> */}

      {/* ===== CTA ===== */}
      <section className="cta-section">
        <h2>Ready to Transform Your Healthcare Practice?</h2>
        <p>
          Partner with our board‑certified team to deploy HIPAA/CMS‑aligned AI that actually ships.
        </p>
        <div className="cta-actions">
          <button className="cta-button" onClick={() => navigate("/form-page")}>Contact Us</button>
          <button className="hero-button alt" onClick={() => navigate("/form-page")}>See a Demo</button>
        </div>
      </section>
    </div>
  );
}

/* ===== Data ===== */
const tiles = [
  { title: "HIPAA", caption: "Privacy by design", img: hipaaIcon },
  { title: "AI Automation", caption: "Safer, faster ops", img: aiAutomationIcon },
  { title: "CMS", caption: "Aligned compliance", img: cms },
  { title: "Growth", caption: "Outcomes & ROI", img: growth }
];

const iconFeatures = [
  { title: "Efficient Healthcare Solutions", text: "Streamlined ops that improve patient outcomes.", icon: efficiencyIcon },
  { title: "Advanced AI & Automation", text: "Enhance diagnostics and automate workflows.", icon: aiAutomationIcon },
  { title: "HIPAA‑Compliant Systems", text: "Security‑first infrastructure ensuring privacy and security.", icon: hipaaIcon },
  { title: "CMS‑Compliant Systems", text: "Aligned compliance and reporting.", icon: cms },
  { title: "Commitment to Growth", text: "Scale intelligently with sustainable success.", icon: growth },
  { title: "Board‑Certified Professionals", text: "Certified experts in healthcare operations and compliance.", icon: board },
  { title: "Program & Project Delivery", text: "Structured delivery from strategy through implementation.", icon: board },
  { title: "Modern Technology Solutions", text: "Secure, scalable systems built for healthcare environments.", icon: board }
];

const spotlights = [
  { title: "Clinical Workflows", blurb: "Automate intake, documentation, and coding.", img: board },
  { title: "Revenue Integrity", blurb: "Reduce denials with proactive AI.", img: growth },
  { title: "Security & Compliance", blurb: "End‑to‑end protection for PHI.", img: hipaaIcon },
  { title: "Analytics & Decisioning", blurb: "Real‑time insights for care teams.", img: aiAutomationIcon }
];

const capabilities = [
  { value: "ai-automation", label: "AI & Workflow Automation" },
  { value: "clinical-docs", label: "Clinical Documentation Optimization" },
  { value: "rcm", label: "Revenue Cycle & Denials Reduction" },
  { value: "security", label: "HIPAA Security & Risk" },
  { value: "cms", label: "CMS Alignment & Reporting" },
  { value: "analytics", label: "Analytics & Operational Dashboards" },
];

const industries = [
  { value: "hospitals", label: "Hospitals & Health Systems" },
  { value: "clinics", label: "Clinics & Outpatient" },
  { value: "private-practice", label: "Private Practice" },
  { value: "billing", label: "Billing / RCM Teams" },
  { value: "specialty", label: "Specialty Groups" },
];

